import React from "react";
import { colors, font, displayName } from "../utils/theme.js";

// Inline, in-row task editors (assignee dropdown + date chips), styled like the status pill.
// Each stops click propagation so editing never opens the row's task panel.

// A task's assignee <-> a flat <select> value: "unassigned" | "client" | <userId>.
export function assigneeToValue(a) {
  if (!a || a.type === "unassigned") return "unassigned";
  if (a.type === "client") return "client";
  return a.userId;
}
export function valueToAssignee(v, clientId) {
  if (v === "unassigned") return { type: "unassigned" };
  if (v === "client") return { type: "client", clientId };
  return { type: "user", userId: v };
}

// "25 September" — day + full month, no year (compact row format).
export function fmtDayMonth(d) {
  if (!d) return "—";
  const dt = new Date(d.slice(0, 10) + "T00:00:00");
  return `${dt.getDate()} ${dt.toLocaleDateString("en-US", { month: "long" })}`;
}

const pill = {
  fontSize: 11, fontFamily: font, border: `1px solid ${colors.border}`, borderRadius: 20,
  padding: "3px 8px", background: "transparent", whiteSpace: "nowrap", cursor: "pointer", outline: "none",
};

export function AssigneeSelect({ assignee, users, clientId, onChange }) {
  const value = assigneeToValue(assignee);
  const list = [...(users || [])].sort((a, b) => displayName(a).localeCompare(displayName(b)));
  // A legacy assignee stored by email (or a removed user) still shows as the selected option.
  const known = value === "unassigned" || value === "client" || list.some((u) => u.id === value);
  const muted = value === "unassigned";
  return (
    <select value={value} aria-label="Assignee"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(valueToAssignee(e.target.value, clientId)); }}
      style={{ ...pill, maxWidth: 150, textOverflow: "ellipsis", color: value === "client" ? colors.warning : muted ? colors.textDim : colors.textMuted, fontWeight: value === "client" ? 700 : 500 }}>
      <option value="unassigned" style={{ color: colors.text, background: colors.surface }}>Unassigned</option>
      {(clientId || value === "client") && <option value="client" style={{ color: colors.text, background: colors.surface }}>Client</option>}
      {!known && <option value={value} style={{ color: colors.text, background: colors.surface }}>{value}</option>}
      {list.map((u) => <option key={u.id} value={u.id} style={{ color: colors.text, background: colors.surface }}>{displayName(u)}</option>)}
    </select>
  );
}

// A date shown as a pill ("Start 25 September", or "+ Start" when unset). Clicking anywhere on
// it opens the browser's calendar; picking a date saves, clearing it saves null.
export function DateChip({ label, value, onChange, danger }) {
  const v = value ? value.slice(0, 10) : "";
  return (
    <span onClick={(e) => e.stopPropagation()} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <span style={{ ...pill, color: danger ? colors.danger : v ? colors.textMuted : colors.textDim, borderStyle: v ? "solid" : "dashed" }}>
        {v ? `${label} ${fmtDayMonth(v)}` : `+ ${label}`}
      </span>
      <input type="date" value={v} aria-label={`${label} date`}
        onClick={(e) => { e.stopPropagation(); try { e.currentTarget.showPicker?.(); } catch { /* older browsers open on click anyway */ } }}
        onChange={(e) => { const nv = e.target.value || null; if (nv !== (v || null)) onChange(nv); }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: "none", padding: 0, margin: 0 }} />
    </span>
  );
}

// Eye / crossed-out eye (stroke icons in the text colour). Crossed out = hidden from the client.
export function EyeIcon({ off = false, size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "-2px", flexShrink: 0 }}>
      {off ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

// Marker on a private task row (or on every row of a private objective).
export function PrivateMark({ objectiveLevel = false }) {
  const title = objectiveLevel ? "Objective is private — hidden from client updates" : "Private — hidden from client updates";
  return <span title={title} aria-label={title} style={{ color: colors.textMuted, flexShrink: 0, lineHeight: 1 }}><EyeIcon off /></span>;
}
